/**
 * O PRODUTO da praça: a lista fechada dos quatro preços que o Rizoma publica, e a
 * ÚNICA tradução item de venda do Cacho → produto que existe.
 *
 * Mora em `shared/` porque os DOIS lados precisam das MESMAS regras:
 *  - o formulário de `/admin/cotacoes` monta uma linha por produto e o servidor
 *    valida contra a mesma lista o que chega no `PUT`;
 *  - o formulário de VENDA do Cacho usa `produtoDoItem` para saber qual preço da
 *    praça pré-preenche cada linha, e o servidor usa a mesma função para agregar
 *    "as minhas vendas × a praça".
 *
 * Duas cópias — uma no client, outra no server — divergiriam no primeiro produto
 * novo do cadastro, e a divergência apareceria como a tela pré-preenchendo um
 * preço que a agregação depois ignora.
 *
 * ⚠️ O QUE NÃO CASA NÃO É INVENTADO. `produtoDoItem` devolve `null`, a agregação
 * IGNORA o item e CONTA quantos ignorou (`itensNaoMapeados`), que a tela escreve
 * no rodapé ("N itens sem produto mapeado"). Silenciar seria a única forma de o
 * número ficar errado sem ninguém ver.
 *
 * Casos que caem em `null` de propósito, e a razão de cada um:
 *  - variedade fora de Prata/Nanica (Maçã está no seed) — a praça não a publica;
 *  - Prata sem classificação — "Prata" não é um dos quatro preços;
 *  - Nanica COM classificação — idem; a praça publica uma Nanica só.
 */

export type Variedade = "PRATA" | "NANICA";

export interface Produto {
  variedade: Variedade;
  /** "1" | "2" | "3" para a Prata; `null` na Nanica. */
  qualidade: string | null;
  /** "Prata 1ª" · "Nanica". */
  label: string;
}

/**
 * Os quatro produtos da praça, nesta ordem — é a ordem do formulário de
 * publicação, do quadro e dos chips. Lista FIXA de propósito: publicar um preço
 * de "Prata 4ª" exigiria mudar aqui, e mudar aqui obriga a olhar o mapeamento.
 */
export const PRODUTOS: Produto[] = [
  { variedade: "PRATA", qualidade: "1", label: "Prata 1ª" },
  { variedade: "PRATA", qualidade: "2", label: "Prata 2ª" },
  { variedade: "PRATA", qualidade: "3", label: "Prata 3ª" },
  { variedade: "NANICA", qualidade: null, label: "Nanica" },
];

/** Um produto identificado por (variedade, qualidade) — o que vai na querystring. */
export interface ChaveProduto {
  variedade: string;
  qualidade: string | null;
}

/** "PRATA"+"1" → "PRATA|1"; "NANICA"+null → "NANICA|". A mesma normalização do
 *  `COALESCE(qualidade, '')` do índice único da migração 056. */
export function chaveProduto(p: ChaveProduto): string {
  return `${p.variedade}|${p.qualidade ?? ""}`;
}

export function labelProduto(p: ChaveProduto): string {
  const achado = PRODUTOS.filter(x => chaveProduto(x) === chaveProduto(p))[0];
  if (achado) return achado.label;
  // Cotação publicada para um produto que saiu da lista (histórico antigo): o
  // rótulo é derivado, nunca "—". Some da lista, não do histórico.
  const variedade = p.variedade === "NANICA" ? "Nanica" : p.variedade === "PRATA" ? "Prata" : p.variedade;
  // O ordinal só vai em qualidade NUMÉRICA ("1" → "1ª"): uma classificação que
  // já é texto ("Extra") não vira "Extraª".
  if (!p.qualidade) return variedade;
  return /^\d+$/.test(p.qualidade) ? `${variedade} ${p.qualidade}ª` : `${variedade} ${p.qualidade}`;
}

/** Devolve o produto da lista com esta chave, ou `null`. Valida o que chega da API. */
export function produtoDaChave(p: ChaveProduto): Produto | null {
  return PRODUTOS.filter(x => chaveProduto(x) === chaveProduto(p))[0] ?? null;
}

// ─── A ponte com o Cacho ─────────────────────────────────────────────────────

/** O item de venda como as agregações o enxergam. Os três campos que o Cacho tem
 *  para dizer "o que é isto" — nenhum deles obrigatório em produção. */
export interface ItemParaProduto {
  /** `variedades.nome` via `produtos_venda.variedade_id`. */
  variedadeNome?: string | null;
  /** `produtos_venda.classificacao`: "1ª" | "2ª" | "3ª" | null. */
  classificacao?: string | null;
  /** `produtos_venda.nome`: "Banana Prata 1ª". Fallback de variedade E de
   *  classificação — produto cadastrado sem `variedade_id` existe em produção. */
  produtoNome?: string | null;
}

/** Minúsculas, sem acento — "Prata-Anã" e "PRATA ANA" têm de cair no mesmo lugar. */
function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/**
 * A variedade da praça dentro de um NOME. "Prata-Anã" e "Prata Catarina" são
 * Prata — a praça publica o preço da Prata, não o da cultivar.
 *
 * Exportada porque a oferta (PR 3) precisa da mesma pergunta sobre a colheita,
 * onde não há item de venda nenhum: lá existe só o nome da variedade. Duas
 * leituras de "isto é Prata?" divergiriam na primeira cultivar nova.
 */
export function variedadeDoNome(nome: string | null | undefined): Variedade | null {
  if (!nome) return null;
  const texto = normalizar(nome);
  if (texto.indexOf("nanica") >= 0) return "NANICA";
  if (texto.indexOf("prata") >= 0) return "PRATA";
  return null;
}

/** A variedade do item: do cadastro de variedades quando existe, do nome do
 *  produto quando não. */
function variedadeDoItem(item: ItemParaProduto): Variedade | null {
  return variedadeDoNome(item.variedadeNome) ?? variedadeDoNome(item.produtoNome);
}

/**
 * A qualidade do item: "1ª" → "1". Lê `classificacao` e, na falta dela, o final
 * do nome do produto ("Banana Prata 2ª"). `undefined` = não há classificação
 * nenhuma; `null` nunca sai daqui (é a Nanica, decidida por `produtoDoItem`).
 */
function qualidadeDoItem(item: ItemParaProduto): string | undefined {
  const daColuna = item.classificacao ? /^\s*([123])\s*[ªaº°]?\s*$/.exec(item.classificacao) : null;
  if (daColuna) return daColuna[1];
  if (item.classificacao) return "?"; // existe, mas não é 1ª/2ª/3ª — não mapeia
  const doNome = item.produtoNome ? /(?:^|\s)([123])\s*[ªaº°](?:\s|$)/.exec(item.produtoNome) : null;
  return doNome ? doNome[1] : undefined;
}

/**
 * O produto da praça correspondente a um item de venda, ou `null`.
 *
 * `null` NÃO é erro nem exceção: é "a praça não publica preço para isto". Quem
 * chama ignora o item e conta (`itensNaoMapeados`). Ver o cabeçalho.
 */
export function produtoDoItem(item: ItemParaProduto): Produto | null {
  const variedade = variedadeDoItem(item);
  if (!variedade) return null;
  const qualidade = qualidadeDoItem(item);

  if (variedade === "NANICA") {
    // Nanica classificada não é a Nanica da praça. Mapear assim mesmo misturaria
    // preços de produtos diferentes numa média ponderada só.
    return qualidade === undefined ? produtoDaChave({ variedade: "NANICA", qualidade: null }) : null;
  }
  if (qualidade === undefined || qualidade === "?") return null;
  return produtoDaChave({ variedade: "PRATA", qualidade });
}

/**
 * O item de venda do Cacho no FORMATO DA PRAÇA — `("Prata", "1ª")` → `("PRATA", "1")`.
 *
 * ⚠️ É O CONSERTO DO "Prata 1ªª" NA ORIGEM. O Cacho guarda a classificação com o
 * ordinal ("1ª", `produtos_venda.classificacao`) e a variedade pelo nome do
 * cadastro ("Prata", "Prata-Anã"); o Mercado inteiro fala a chave da praça
 * (`"PRATA"`, `"1"`), que é o que o registro próprio grava. Quando o romaneio do
 * Rizoma entrava nos documentos do comprador sem tradução, `labelProduto` não o
 * achava na lista e o fallback acrescentava o "ª" que já estava lá — e a mesma
 * Prata virava duas barras no tile de volume ("Prata" e "PRATA").
 *
 * Mapeia pela ÚNICA tradução que existe (`produtoDoItem`). O que a praça não
 * publica (Maçã, Nanica classificada) sai com a variedade do cadastro e a
 * qualidade SEM o ordinal — o mesmo formato, para `labelProduto` pôr o "ª" uma vez.
 */
export function chaveDaPraca(item: ItemParaProduto): ChaveProduto {
  const produto = produtoDoItem(item);
  if (produto) return { variedade: produto.variedade, qualidade: produto.qualidade };
  const variedade = (item.variedadeNome ?? "").trim() || (item.produtoNome ?? "").trim() || "—";
  const bruta = (item.classificacao ?? "").trim();
  const numero = /^(\d+)\s*[ªaº°]?$/.exec(bruta);
  return { variedade, qualidade: numero ? numero[1] : (bruta || null) };
}

/** "Prata 1ª" para um item no formato do CACHO (`variedade` do cadastro, `qualidade` com ordinal). */
export function rotuloItemDeVenda(item: { variedade: string | null; qualidade: string | null }): string {
  return labelProduto(chaveDaPraca({ variedadeNome: item.variedade, classificacao: item.qualidade, produtoNome: null }));
}
